<script setup lang="ts">
import { computed, useId } from 'vue'
import { maskPhone, phoneDigits } from '../phone'

// type="tel": the field shows +55 47 98888-1296 and the model holds only the digits (5547988881296).

const props = withDefaults(
  defineProps<{
    label: string
    type?: 'text' | 'email' | 'tel' | 'search' | 'password' | 'url'
    placeholder?: string
    hint?: string
    error?: string
    autocomplete?: string
    inputmode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal' | 'search' | 'url'
    disabled?: boolean
    required?: boolean
    maxlength?: number
    id?: string
  }>(),
  { type: 'text' },
)

const model = defineModel<string>({ default: '' })

const isPhone = computed(() => props.type === 'tel')

function onPhoneInput(event: Event) {
  const input = event.target as HTMLInputElement
  // Keeps the caret after the same digit once the mask is applied again.
  const digitsBeforeCaret = phoneDigits(input.value.slice(0, input.selectionStart ?? input.value.length)).length
  const digits = phoneDigits(input.value)
  const masked = maskPhone(digits)
  model.value = digits
  input.value = masked
  let caret = 0
  for (let seen = 0; caret < masked.length && seen < digitsBeforeCaret; caret++) if (/\d/.test(masked.charAt(caret))) seen++
  input.setSelectionRange(caret, caret)
}

const generatedId = useId()
const inputId = computed(() => props.id ?? `field-${generatedId}`)
const describedBy = computed(() =>
  [props.error ? `${inputId.value}-error` : null, props.hint ? `${inputId.value}-hint` : null].filter(Boolean).join(' ') ||
  undefined,
)
</script>

<template>
  <div class="ui-field">
    <label :for="inputId" class="ui-field__label">
      {{ label }}
      <slot name="label-extra" />
    </label>
    <input
      v-if="isPhone"
      :id="inputId"
      :value="maskPhone(model)"
      class="ui-field__input"
      :class="{ 'ui-field__input--invalid': !!error }"
      type="tel"
      :placeholder="placeholder ?? '+55 47 98888-1296'"
      :autocomplete="autocomplete"
      inputmode="tel"
      :disabled="disabled"
      :required="required"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
      @input="onPhoneInput"
    />
    <input
      v-else
      :id="inputId"
      v-model="model"
      class="ui-field__input"
      :class="{ 'ui-field__input--invalid': !!error }"
      :type="type"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :disabled="disabled"
      :required="required"
      :maxlength="maxlength"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
    />
    <span v-if="error" :id="`${inputId}-error`" role="alert" class="ui-field__error">{{ error }}</span>
    <span v-if="hint" :id="`${inputId}-hint`" class="ui-field__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-field__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-field__input {
  height: var(--field-height);
  padding: 0 12px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font-size: var(--type-body-size);
  color: var(--text-primary);
  background: var(--surface-card);
  width: 100%;
}
.ui-field__input::placeholder {
  color: var(--text-placeholder);
}
.ui-field__input--invalid {
  border-color: var(--error);
}
.ui-field__input:disabled {
  opacity: 0.6;
}
.ui-field__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-field__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-field__input:focus {
  outline: none;
  border-color: var(--accent);
}
</style>
